import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { TackLogo } from "@/components/TackLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

type ExchangeState = "pending" | "ready" | "error";

function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [exchangeState, setExchangeState] = useState<ExchangeState>("pending");
  const [exchangeError, setExchangeError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    async function establishSession() {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");
        const tokenHash = url.searchParams.get("token_hash");
        const type = url.searchParams.get("type");
        const hash = window.location.hash;

        const { data: existing } = await supabase.auth.getSession();
        if (existing.session) {
          if (!cancelled) setExchangeState("ready");
          return;
        }

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (tokenHash && (type === "recovery" || type === "email")) {
          const { error } = await supabase.auth.verifyOtp({
            type: "recovery",
            token_hash: tokenHash,
          });
          if (error) throw error;
        } else if (hash.includes("access_token") && hash.includes("type=recovery")) {
          await new Promise((r) => setTimeout(r, 200));
          const { data: retry } = await supabase.auth.getSession();
          if (!retry.session) throw new Error(t('auth.recoveryVerifyFailed'));
        } else {
          throw new Error(t('auth.recoveryInvalid'));
        }

        window.history.replaceState({}, "", "/reset-password");
        if (!cancelled) setExchangeState("ready");
      } catch (err: any) {
        console.error("Reset password session exchange failed:", err);
        if (!cancelled) {
          setExchangeError(err?.message || t('auth.recoveryExpired'));
          setExchangeState("error");
        }
      }
    }

    establishSession();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error(t('auth.passwordsMismatch'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('auth.passwordMin6'));
      return;
    }
    setLoading(true);
    try {
      await updatePassword(password);
      toast.success(t('auth.passwordUpdated'));
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      toast.error(err.message || t('auth.updatePasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <TackLogo size="lg" />
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="font-display">{t('auth.setNewPassword')}</CardTitle>
          </CardHeader>
          <CardContent>
            {exchangeState === "pending" && (
              <p className="text-center text-sm text-muted-foreground py-4">
                {t('auth.verifyingLink')}
              </p>
            )}
            {exchangeState === "error" && (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive">{exchangeError}</p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate({ to: "/login" })}
                >
                  {t('auth.backToLoginBtn')}
                </Button>
              </div>
            )}
            {exchangeState === "ready" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.newPasswordLabel')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">{t('auth.confirmPasswordLabel')}</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('auth.updating') : t('auth.updatePasswordBtn')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
